import parse, { Element, type HTMLReactParserOptions } from 'html-react-parser';

import { cn } from '@design-system/helpers';

/** Tags that can run code or pull in resources; a rich text never needs them. */
const BLOCKED_TAGS = new Set([
  'script',
  'style',
  'iframe',
  'object',
  'embed',
  'link',
  'meta',
  'base',
  'form',
  'input',
  'button',
]);

const URL_ATTRIBUTES = new Set(['href', 'src', 'xlink:href', 'formaction']);

const options: HTMLReactParserOptions = {
  replace(node) {
    if (!(node instanceof Element)) return undefined;
    if (BLOCKED_TAGS.has(node.name)) return <></>;
    Object.keys(node.attribs).forEach((name) => {
      const value = node.attribs[name];
      const isHandler = name.toLowerCase().startsWith('on');
      const isScriptUrl =
        URL_ATTRIBUTES.has(name.toLowerCase()) &&
        /^\s*(javascript|data|vbscript):/i.test(value);
      if (isHandler || isScriptUrl) delete node.attribs[name];
    });
    return undefined;
  },
};

/**
 * Renders editor HTML the server stored as sent. The knowledge base API
 * documents that rich text content "must be sanitised before it is rendered
 * back", and this is the one place that does it.
 */
export function SafeHtml({
  html,
  className,
}: {
  html: string;
  className?: string;
}) {
  return (
    <div
      data-slot='safe-html'
      className={cn(
        'prose prose-sm max-w-none text-gray-700 [&_a]:text-primary-800 [&_a]:underline [&_ol]:list-decimal [&_ol]:pl-5 [&_ul]:list-disc [&_ul]:pl-5',
        className
      )}
    >
      {parse(html, options)}
    </div>
  );
}
